import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';

interface Factor {
  id: string;
  evaluation_id: string;
  name: string;
  factor_type: 'RATING' | 'YES_NO' | 'TRUE_FALSE';
  rating_group_id: string | null;
  score_percent: string;
}
interface Evaluation {
  id: string;
  name: string;
  score_percent: string;
  factors: Factor[];
}
interface Detail {
  assignment: any;
  template: { evaluations: Evaluation[]; qualitativeItems: { id: string; item_text: string }[]; qualitativeEnabled: boolean };
  levels: any[];
}
interface Rating {
  id: string;
  description: string;
  definition: string;
  score_percent: string;
}
interface QueueItem {
  assignmentId: string;
  employeeCode?: string;
  employeeName?: string;
  templateName?: string;
  mode: 'self' | 'approve';
  approvalInstanceId?: string;
  isFinalLevel?: boolean;
}

export default function ApproveAppraisal() {
  const [selfQueue, setSelfQueue] = useState<QueueItem[]>([]);
  const [approvalQueue, setApprovalQueue] = useState<QueueItem[]>([]);
  const [active, setActive] = useState<QueueItem | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [step, setStep] = useState(1);
  const [ratingsByGroup, setRatingsByGroup] = useState<Record<string, Rating[]>>({});

  const [factorInputs, setFactorInputs] = useState<Record<string, { ratingId?: string; boolValue?: boolean }>>({});
  const [evaluationComments, setEvaluationComments] = useState('');
  const [qualComments, setQualComments] = useState<Record<string, string>>({});
  const [recommendation, setRecommendation] = useState({ promotion: false, incrementPercent: '', incrementAmount: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadQueues = async () => {
    const [selfRes, approveRes] = await Promise.all([
      api.get('/pas/assignments/my-self-appraisals'),
      api.get('/pas/assignments/my-approvals'),
    ]);
    setSelfQueue(
      (selfRes.data.assignments || []).map((a: any) => ({
        assignmentId: a.id,
        templateName: a.template_name,
        mode: 'self' as const,
      }))
    );
    setApprovalQueue(
      (approveRes.data.approvals || []).map((a: any) => ({
        assignmentId: a.assignment_id,
        employeeCode: a.employee_code,
        employeeName: a.employee_name,
        templateName: a.template_name,
        mode: 'approve' as const,
        approvalInstanceId: a.approvalInstanceId,
        isFinalLevel: a.isFinalLevel,
      }))
    );
  };

  useEffect(() => {
    loadQueues();
  }, []);

  const open = async (item: QueueItem) => {
    setActive(item);
    setStep(1);
    setError('');
    setFactorInputs({});
    setEvaluationComments('');
    setQualComments({});
    setRecommendation({ promotion: false, incrementPercent: '', incrementAmount: '', notes: '' });
    const res = await api.get(`/pas/assignments/${item.assignmentId}`);
    const d: Detail = res.data;
    setDetail(d);

    const groupIds = Array.from(
      new Set(
        d.template.evaluations.flatMap((e) => e.factors).filter((f) => f.factor_type === 'RATING' && f.rating_group_id).map((f) => f.rating_group_id as string)
      )
    );
    const loaded: Record<string, Rating[]> = {};
    for (const groupId of groupIds) {
      const r = await api.get(`/pas/masters/rating-groups/${groupId}/ratings`);
      loaded[groupId] = r.data.ratings || [];
    }
    setRatingsByGroup(loaded);
  };

  const setFactorRating = (factorId: string, ratingId: string) => {
    setFactorInputs((prev) => ({ ...prev, [factorId]: { ratingId } }));
  };
  const setFactorBool = (factorId: string, boolValue: boolean) => {
    setFactorInputs((prev) => ({ ...prev, [factorId]: { boolValue } }));
  };

  const allFactors = detail ? detail.template.evaluations.flatMap((e) => e.factors) : [];
  const allRated = allFactors.every((f) => {
    const input = factorInputs[f.id];
    if (!input) return false;
    return f.factor_type === 'RATING' ? !!input.ratingId : input.boolValue !== undefined;
  });

  // Client-side preview only — the authoritative score is computed server-side on submit.
  const previewScore = () => {
    if (!detail) return 0;
    let total = 0;
    for (const ev of detail.template.evaluations) {
      for (const f of ev.factors) {
        const input = factorInputs[f.id];
        let achieved = 0;
        if (f.factor_type === 'RATING' && input?.ratingId) {
          const group = f.rating_group_id ? ratingsByGroup[f.rating_group_id] || [] : [];
          achieved = Number(group.find((r) => r.id === input.ratingId)?.score_percent || 0);
        } else if (f.factor_type !== 'RATING' && input?.boolValue !== undefined) {
          achieved = input.boolValue ? 100 : 0;
        }
        const weight = (Number(ev.score_percent) * Number(f.score_percent)) / 100;
        total += (achieved * weight) / 100;
      }
    }
    return Math.round(total * 100) / 100;
  };

  const allQualComplete = !detail?.template.qualitativeEnabled || detail.template.qualitativeItems.every((q) => (qualComments[q.id] || '').trim());

  const submit = async (action: 'APPROVED' | 'REJECTED' = 'APPROVED') => {
    if (!active || !detail) return;
    setSubmitting(true);
    setError('');
    try {
      const factorScores = allFactors.map((f) => ({ factorId: f.id, ...factorInputs[f.id] }));
      const qualitativeResponses = detail.template.qualitativeItems.map((q) => ({ qualitativeItemId: q.id, comments: qualComments[q.id] || '' }));

      if (active.mode === 'self') {
        await api.post(`/pas/assignments/${active.assignmentId}/self-appraisal`, {
          factorScores,
          evaluationComments,
          qualitativeResponses,
        });
      } else {
        const body: any = { action, comments: evaluationComments, factorScores, evaluationComments, qualitativeResponses };
        if (action === 'APPROVED' && active.isFinalLevel) {
          body.recommendation = {
            promotion: recommendation.promotion,
            incrementPercent: recommendation.incrementPercent ? Number(recommendation.incrementPercent) : null,
            incrementAmount: recommendation.incrementAmount ? Number(recommendation.incrementAmount) : null,
            notes: recommendation.notes,
          };
        }
        await api.post(`/pas/assignments/${active.assignmentId}/appraise`, body);
      }
      setActive(null);
      setDetail(null);
      loadQueues();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Approve Appraisal</h1>

      {!active && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="font-semibold mb-2">My Self Appraisals</h2>
            <div className="bg-white rounded-lg shadow divide-y">
              {selfQueue.length === 0 && <div className="p-4 text-sm text-gray-500">Nothing pending</div>}
              {selfQueue.map((item) => (
                <button key={item.assignmentId} onClick={() => open(item)} className="w-full text-left p-4 hover:bg-gray-50">
                  {item.templateName}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-semibold mb-2">Pending My Approval</h2>
            <div className="bg-white rounded-lg shadow divide-y">
              {approvalQueue.length === 0 && <div className="p-4 text-sm text-gray-500">Nothing pending</div>}
              {approvalQueue.map((item) => (
                <button key={item.assignmentId} onClick={() => open(item)} className="w-full text-left p-4 hover:bg-gray-50">
                  {item.employeeCode} — {item.employeeName} ({item.templateName}) {item.isFinalLevel && <span className="text-xs text-blue-600">Final Level</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {active && detail && (
        <div className="bg-white rounded-lg shadow p-6 max-w-3xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold">
              {active.mode === 'self' ? 'Self Appraisal' : `${active.employeeName} (${active.employeeCode})`} — {detail.assignment.template_name}
            </h2>
            <Button variant="secondary" onClick={() => { setActive(null); setDetail(null); }}>Close</Button>
          </div>

          <div className="flex gap-4 mb-6 text-sm">
            <span className={step === 1 ? 'font-bold text-blue-600' : 'text-gray-400'}>1. Quantitative</span>
            <span className={step === 2 ? 'font-bold text-blue-600' : 'text-gray-400'}>2. Qualitative</span>
            {active.mode === 'approve' && active.isFinalLevel && (
              <span className={step === 3 ? 'font-bold text-blue-600' : 'text-gray-400'}>3. Recommendation</span>
            )}
          </div>

          {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-4 py-2">{error}</div>}

          {step === 1 && (
            <div className="space-y-6">
              {detail.template.evaluations.map((ev) => (
                <div key={ev.id}>
                  <h3 className="font-medium mb-2">{ev.name} ({ev.score_percent}%)</h3>
                  <div className="space-y-3">
                    {ev.factors.map((f) => (
                      <div key={f.id} className="border rounded p-3">
                        <div className="text-sm font-medium mb-1">{f.name} ({f.score_percent}%)</div>
                        {f.factor_type === 'RATING' ? (
                          <div className="flex flex-wrap gap-2">
                            {(f.rating_group_id ? ratingsByGroup[f.rating_group_id] || [] : []).map((r) => (
                              <label key={r.id} className="flex items-center gap-1 text-xs border rounded px-2 py-1 cursor-pointer" title={r.definition}>
                                <input
                                  type="radio"
                                  name={`factor-${f.id}`}
                                  checked={factorInputs[f.id]?.ratingId === r.id}
                                  onChange={() => setFactorRating(f.id, r.id)}
                                />
                                {r.description} ({r.score_percent}%)
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="flex gap-3 text-sm">
                            <label className="flex items-center gap-1">
                              <input type="radio" name={`factor-${f.id}`} checked={factorInputs[f.id]?.boolValue === true} onChange={() => setFactorBool(f.id, true)} />
                              {f.factor_type === 'YES_NO' ? 'Yes' : 'True'}
                            </label>
                            <label className="flex items-center gap-1">
                              <input type="radio" name={`factor-${f.id}`} checked={factorInputs[f.id]?.boolValue === false} onChange={() => setFactorBool(f.id, false)} />
                              {f.factor_type === 'YES_NO' ? 'No' : 'False'}
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <label className="text-sm font-medium text-gray-700">Evaluation Comments</label>
                <textarea className="w-full border rounded-lg px-3 py-2 mt-1" rows={3} value={evaluationComments} onChange={(e) => setEvaluationComments(e.target.value)} />
              </div>
              <div className="flex justify-end">
                <Button disabled={!allRated} onClick={() => setStep(2)}>Next: Qualitative</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm">
                Computed Quantitative Score: <strong>{previewScore()}%</strong>
              </div>
              {detail.template.qualitativeEnabled ? (
                detail.template.qualitativeItems.map((q) => (
                  <div key={q.id}>
                    <label className="text-sm font-medium text-gray-700">{q.item_text} <span className="text-red-500">*</span></label>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 mt-1"
                      rows={2}
                      value={qualComments[q.id] || ''}
                      onChange={(e) => setQualComments({ ...qualComments, [q.id]: e.target.value })}
                    />
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">This template has no qualitative section.</div>
              )}
              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
                {active.mode === 'approve' && active.isFinalLevel ? (
                  <Button disabled={!allQualComplete} onClick={() => setStep(3)}>Next: Recommendation</Button>
                ) : (
                  <div className="flex gap-2">
                    {active.mode === 'approve' && (
                      <Button variant="danger" loading={submitting} onClick={() => submit('REJECTED')}>Reject</Button>
                    )}
                    <Button disabled={!allQualComplete} loading={submitting} onClick={() => submit('APPROVED')}>Submit</Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && active.mode === 'approve' && active.isFinalLevel && (
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={recommendation.promotion} onChange={(e) => setRecommendation({ ...recommendation, promotion: e.target.checked })} />
                Recommend Promotion
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Increment %" type="number" value={recommendation.incrementPercent} onChange={(e) => setRecommendation({ ...recommendation, incrementPercent: e.target.value })} />
                <Input label="Increment Amount" type="number" value={recommendation.incrementAmount} onChange={(e) => setRecommendation({ ...recommendation, incrementAmount: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Other Adjustments / Notes</label>
                <textarea className="w-full border rounded-lg px-3 py-2 mt-1" rows={3} value={recommendation.notes} onChange={(e) => setRecommendation({ ...recommendation, notes: e.target.value })} />
              </div>
              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep(2)}>Back</Button>
                <div className="flex gap-2">
                  <Button variant="danger" loading={submitting} onClick={() => submit('REJECTED')}>Reject</Button>
                  <Button loading={submitting} onClick={() => submit('APPROVED')}>Submit</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
